import { useEffect, useState } from "react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { money } from "@/components/shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet } from "lucide-react";

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
  return { value: format(d, "yyyy-MM"), label: format(d, "MMMM yyyy") };
});
const pct = (v) => (v == null ? "—" : `${v}%`);
const tone = (v) => (v == null ? "" : v < 0 ? "text-destructive" : "text-[hsl(140_60%_32%)]");

export default function MarginTable() {
  const [month, setMonth] = useState(MONTHS[0].value);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/margin", { params: { month } }).then((r) => setData(r.data)).catch(() => {}); }, [month]);

  return (
    <div className="mt-12" data-testid="margin-table">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="label-caps text-muted-foreground flex items-center gap-2"><Wallet className="w-4 h-4 text-brand" /> Payroll & margin (ex GST)</div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="margin-month"
          className="border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand">
          {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div className="rounded-sm border border-border/60 bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Cleaner</TableHead><TableHead className="text-right">Booked</TableHead><TableHead className="text-right">Completed</TableHead>
            <TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Pay</TableHead>
            <TableHead className="text-right">Margin</TableHead><TableHead className="text-right">Margin %</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.rows || []).map((r) => (
              <TableRow key={r.staff_id} data-testid={`margin-row-${r.staff_id}`}>
                <TableCell><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color }} />{r.name}</span></TableCell>
                <TableCell className="text-right">{money(r.booked_revenue)}</TableCell>
                <TableCell className="text-right">{money(r.completed_revenue)}</TableCell>
                <TableCell className="text-right">{r.hours} h{r.hours_estimated > 0 && <span className="text-[10px] text-muted-foreground" title="Includes planned hours where there was no check-in"> *</span>}</TableCell>
                <TableCell className="text-right">{money(r.hourly_rate)}/h</TableCell>
                <TableCell className="text-right" data-testid={`margin-pay-${r.staff_id}`}>{money(r.pay)}</TableCell>
                <TableCell className={`text-right font-medium ${tone(r.margin_pct)}`} data-testid={`margin-value-${r.staff_id}`}>{money(r.margin)}</TableCell>
                <TableCell className={`text-right ${tone(r.margin_pct)}`}>{pct(r.margin_pct)}</TableCell>
              </TableRow>
            ))}
            {data && (
              <TableRow className="bg-[hsl(330_60%_97%)] font-medium" data-testid="margin-totals">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{money(data.totals.booked_revenue)}</TableCell>
                <TableCell className="text-right">{money(data.totals.completed_revenue)}</TableCell>
                <TableCell className="text-right">{data.totals.hours} h</TableCell>
                <TableCell />
                <TableCell className="text-right">{money(data.totals.pay)}</TableCell>
                <TableCell className={`text-right ${tone(data.totals.margin_pct)}`}>{money(data.totals.margin)}</TableCell>
                <TableCell className={`text-right ${tone(data.totals.margin_pct)}`}>{pct(data.totals.margin_pct)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        Margin = completed revenue − pay. Pay uses actual checked-in hours × hourly rate (* = planned hours used where no check-in). Shared bookings split revenue equally between cleaners.
      </p>
    </div>
  );
}
